<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher } from 'svelte';
  import ScoreFilter from './ScoreFilter.svelte';
  import { applicationMode } from '$lib/stores/mode';
  import { embeddingQuery } from '$lib/stores/selection';

  export let sortBy = 'quality'; // quality, size, name
  export let sortOrder = 'desc'; // asc, desc

  const dispatch = createEventDispatcher();

  // Determine if score filter should be shown
  $: showScoreFilter =
    $applicationMode === 'neighbor' ||
    ($applicationMode === 'classic' && $embeddingQuery && $embeddingQuery.trim() !== '');

  function handleSortChange(newSortBy: string) {
    if (sortBy === newSortBy) {
      // Toggle order if same field
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New field, default to appropriate order
      sortBy = newSortBy;
      sortOrder = newSortBy === 'name' ? 'asc' : 'desc';
    }

    dispatch('sortChange', { sortBy, sortOrder });
  }

  $: sortOptions = [
    {
      value: 'quality',
      label: $_('sorting.matchScore'),
      icon: `<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>`
    },
    {
      value: 'size',
      label: $_('sorting.companySize'),
      icon: `<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>`
    },
    {
      value: 'name',
      label: $_('sorting.alphabetical'),
      icon: `<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>`
    }
  ];
</script>

<div class="mb-3 rounded-control border border-gray-200 bg-white p-2">
  <!-- Mobile layout -->
  <div class="flex flex-col gap-3 sm:hidden">
    <!-- Sort buttons row - full width on mobile -->
    <div class="grid grid-cols-3 gap-1">
      {#each sortOptions as option}
        <button
          class="group relative flex flex-col items-center justify-center gap-1 rounded-control px-2 py-2 text-xs font-light transition-colors duration-200
            {sortBy === option.value
            ? 'border border-accent-200 bg-accent-100 text-accent-900 '
            : 'border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'}"
          on:click={() => handleSortChange(option.value)}
          type="button"
        >
          <span class="inline-block h-4 w-4">
            {@html option.icon}
          </span>
          <span class="text-2xs">{option.label}</span>
          {#if sortBy === option.value}
            <svg
              class="absolute right-1 top-1 h-2.5 w-2.5 transition-transform duration-200"
              class:rotate-180={sortOrder === 'asc'}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          {/if}
        </button>
      {/each}
    </div>

    <!-- Score filter and sort order on mobile -->
    {#if showScoreFilter}
      <ScoreFilter />
    {/if}
    <div class="text-center text-xs text-gray-500">
      {#if sortBy === 'quality'}
        {sortOrder === 'desc' ? $_('sorting.bestFirst') : $_('sorting.worstFirst')}
      {:else if sortBy === 'size'}
        {sortOrder === 'desc' ? $_('sorting.largestFirst') : $_('sorting.smallestFirst')}
      {:else if sortBy === 'name'}
        {sortOrder === 'desc' ? $_('sorting.zToA') : $_('sorting.aToZ')}
      {/if}
    </div>
  </div>

  <!-- Desktop layout -->
  <div class="hidden sm:flex sm:items-center sm:gap-2">
    <span class="text-xs font-medium text-gray-600">{$_('sorting.sortBy')}:</span>

    <div class="flex items-center gap-1">
      {#each sortOptions as option}
        <div
          class="group relative flex cursor-pointer select-none items-center gap-1 rounded-full px-3 py-1 text-xs font-light transition-colors duration-200 {sortBy ===
          option.value
            ? 'border border-accent-200 bg-accent-100 text-accent-900 '
            : 'border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-900'}"
          on:click={() => handleSortChange(option.value)}
          role="button"
          tabindex="0"
          on:keydown={(e) => e.key === 'Enter' && handleSortChange(option.value)}
        >
          <span class="inline-block h-4 w-4">
            {@html option.icon}
          </span>
          <span>{option.label}</span>

          {#if sortBy === option.value}
            <svg
              class="ml-0.5 h-3 w-3 transition-transform duration-200"
              class:rotate-180={sortOrder === 'asc'}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          {:else}
            <svg
              class="ml-0.5 h-3 w-3 opacity-0 transition-opacity group-hover:opacity-30"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          {/if}
        </div>
      {/each}
    </div>

    <div class="ml-auto flex items-center gap-4">
      <!-- Score Filter -->
      {#if showScoreFilter}
        <ScoreFilter />
      {/if}

      <!-- Sort order text -->
      <div class="text-xs text-gray-500">
        {#if sortBy === 'quality'}
          {sortOrder === 'desc' ? $_('sorting.bestFirst') : $_('sorting.worstFirst')}
        {:else if sortBy === 'size'}
          {sortOrder === 'desc' ? $_('sorting.largestFirst') : $_('sorting.smallestFirst')}
        {:else if sortBy === 'name'}
          {sortOrder === 'desc' ? $_('sorting.zToA') : $_('sorting.aToZ')}
        {/if}
      </div>
    </div>
  </div>
</div>
